import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCheck,
  faDownload,
  faHandshake,
  faIndustry,
  faMedal,
  faTruckFast,
  faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import '../styles/HomePage.css';

const CountUpNumber = ({ end, prefix = '', suffix = '', duration = 1500, start, locale = 'tr-TR' }) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!start) {
      return;
    }

    let animationFrame;
    const startedAt = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(end * eased));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(tick);
      }
    };

    animationFrame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(animationFrame);
  }, [duration, end, start]);

  return (
    <strong>
      {prefix}
      {value.toLocaleString(locale)}
      {suffix && <span>{suffix}</span>}
    </strong>
  );
};

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const [statsStarted, setStatsStarted] = useState(false);
  const numberLocale = i18n.language === 'en' ? 'en-US' : 'tr-TR';

  const products = [
    {
      id: 1,
      title: t('home.hero.product_ceiling'),
      description: t('home.products.ceiling_desc'),
      image: '/images/1-2.png'
    },
    {
      id: 2,
      title: t('home.hero.product_wall'),
      description: t('home.products.wall_desc'),
      image: '/images/1-3.png'
    },
    {
      id: 3,
      title: t('home.hero.product_plaster'),
      description: t('home.products.plaster_desc'),
      image: '/images/1-1.png'
    }
  ];

  const stats = [
    { end: 100000, suffix: t('home.stats.meter_per_day'), label: t('home.stats.capacity'), icon: faIndustry },
    { end: 1500, suffix: '+', label: t('home.stats.active_customers'), icon: faHandshake },
    { end: 100, prefix: '%', label: t('home.stats.satisfaction'), icon: faMedal },
    { end: 100, prefix: '%', label: t('home.stats.on_time_delivery'), icon: faTruckFast }
  ];

  const profileLines = [
    t('home.hero.product_ceiling'),
    t('home.hero.product_wall'),
    t('home.hero.product_plaster')
  ];

  const documentGroups = [
    {
      title: t('home.documents.catalogs.title'),
      description: t('home.documents.catalogs.description'),
      files: [
        { label: t('home.documents.catalogs.product_catalog'), href: '/docs/kusgrup-urun-katalogu.pdf' },
        { label: t('home.documents.catalogs.profile_catalog'), href: '/docs/kusgrup-profil-katalogu.pdf' }
      ]
    },
    {
      title: t('home.documents.certificates.title'),
      description: t('home.documents.certificates.description'),
      files: [
        { label: t('home.documents.certificates.quality_documents'), href: '/docs/kusgrup-kalite-belgeleri.pdf' },
        { label: t('home.documents.certificates.technical_documents'), href: '/docs/kusgrup-teknik-belgeler.pdf' }
      ]
    }
  ];

  return (
    <div className="home-page">
      <section className="home-hero">
        <div
          className="home-hero-bg"
          style={{
            backgroundImage:
              "linear-gradient(90deg, rgba(8, 13, 24, 0.95) 0%, rgba(13, 19, 31, 0.88) 42%, rgba(11, 17, 29, 0.48) 100%), url('/back.png')"
          }}
        />
        <div className="container home-hero-container">
          <motion.div
            className="home-hero-content"
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <h1>{t('home.hero.title')}</h1>
            <ul className="hero-feature-list">
              {profileLines.map((item) => (
                <li key={item}>
                  <FontAwesomeIcon icon={faCheck} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="hero-actions">
              <Link to="/contact" className="hero-btn hero-btn-primary">
                {t('home.hero.cta_quote')}
              </Link>
              <Link to="/products" className="hero-btn hero-btn-outline">
                <FontAwesomeIcon icon={faDownload} />
                {t('home.hero.cta_price_list')}
              </Link>
            </div>
          </motion.div>

          <motion.div
            className="profile-visual single-profile-visual"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            <img src="/images/1-2.png" alt={t('home.hero.image_alt')} />
          </motion.div>
        </div>
      </section>

      <section className="home-stats" aria-label={t('home.stats.aria_label')}>
        <div className="container">
          <motion.div
            className="stats-panel"
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            onViewportEnter={() => setStatsStarted(true)}
          >
            {stats.map((stat) => (
              <div className="stat-item" key={stat.label}>
                <FontAwesomeIcon icon={stat.icon} />
                <div>
                  <CountUpNumber
                    end={stat.end}
                    prefix={stat.prefix}
                    suffix={stat.suffix}
                    start={statsStarted}
                    locale={numberLocale}
                  />
                  <p>{stat.label}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="home-products">
        <div className="container">
          <div className="home-section-heading">
            <span />
            <h2>{t('home.products.title')}</h2>
            <span />
            <p>{t('home.products.subtitle')}</p>
          </div>

          <div className="home-product-grid">
            {products.map((product, index) => (
              <motion.article
                className="home-product-card"
                key={product.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.12 }}
              >
                <div className="product-card-image">
                  <img src={product.image} alt={product.title} />
                </div>
                <div className="product-card-content">
                  <h3>{product.title}</h3>
                  <p>{product.description}</p>
                  <Link to="/products" className="product-card-link">
                    {t('home.products.detail')}
                    <FontAwesomeIcon icon={faArrowRight} />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>

          <div className="home-documents-heading">
            <span />
            <h2>{t('home.documents.title')}</h2>
            <span />
            <p>{t('home.documents.subtitle')}</p>
          </div>

          <div className="home-documents-grid">
            {documentGroups.map((group, index) => (
              <motion.article
                className="home-document-card"
                key={group.title}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.12 }}
              >
                <div className="document-card-header">
                  <span className="document-card-icon">
                    <FontAwesomeIcon icon={faFilePdf} />
                  </span>
                  <div>
                    <h3>{group.title}</h3>
                    <p>{group.description}</p>
                  </div>
                </div>

                <div className="document-links">
                  {group.files.map((file) => (
                    <a key={file.href} href={file.href} target="_blank" rel="noopener noreferrer">
                      <span>{file.label}</span>
                      <FontAwesomeIcon icon={faDownload} />
                    </a>
                  ))}
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
